class BuyButtonConfigBuilder {
  constructor(config = { ctaText: 'productPage.nyPublication.buyBox.buttonLabel' }) {
    if (!config.ctaText) {
      throw new Error('ctaText is required');
    }
    this.rules = [];
    this.config = {
      ctaText: config.ctaText,
      ownedCtaText: config.ownedCtaText || null,
      ctaDisabledTooltip: config.ctaDisabledTooltip || 'productPage.common.buyBox.buttonDisabled.tooltip'
    };
  }

  // Jurisdiction rules
  jurisdictionIs(targetJurisdiction, customTooltip) {
    this.rules.push({
      type: 'jurisdiction_exact',
      value: targetJurisdiction,
      errorTooltip: customTooltip || 'productPage.common.buyBox.buttonDisabled.tooltip.jurisdictionRestricted'
    });
    return this;
  }

  jurisdictionIn(allowedJurisdictions, customTooltip) {
    this.rules.push({
      type: 'jurisdiction_in_list',
      value: allowedJurisdictions,
      errorTooltip: customTooltip || 'productPage.common.buyBox.buttonDisabled.tooltip.jurisdictionRestricted'
    });
    return this;
  }

  // Entitlement rules for specific products
  productNotPurchased(service, customTooltip) {
    this.rules.push({
      type: 'product_not_purchased',
      value: service,
      errorTooltip: customTooltip || 'productPage.common.buyBox.buttonDisabled.tooltip'
    });
    return this;
  }

  // Rule for requiring a specific product as prerequisite
  requiresProduct(service, customTooltip, customCtaText) {
    this.rules.push({
      type: 'requires_product',
      value: service,
      customCtaText: customCtaText || this.config.ctaText,
      errorTooltip: customTooltip || 'productPage.common.buyBox.buttonDisabled.tooltip.missingPrerequisite'
    });
    return this;
  }

  // Entitlement rule for current product (dynamic)
  currentProductNotPurchased(customTooltip) {
    this.rules.push({
      type: 'current_product_not_purchased',
      errorTooltip: customTooltip // Don't default here - let build() handle fallback to config tooltip
    });
    return this;
  }

  // Entitlement rule for current product (dynamic) allow navigate to url review purchased
  allowReviewPurchased() {
    this.rules.push({
      type: 'allow_review_purchased',
      value: true
    });
    return this;
  }

  allowMultiplePurchases() {
    this.rules.push({
      type: 'allow_multiple_purchases',
      value: true
    });
    return this;
  }

  formedByZb(customTooltip) {
    this.rules.push({
      type: 'formed_by_zb',
      errorTooltip: customTooltip || 'productPage.common.buyBox.buttonDisabled.tooltip.businessNotFormedByZb'
    });
    // This product is only available for businesses formed with ZenBusiness.
    return this;
  }

  businessFormed(customTooltip) {
    this.rules.push({
      type: 'business_formed',
      errorTooltip: customTooltip || 'productPage.common.buyBox.buttonDisabled.tooltip.businessNotFormed'
    });
    return this;
  }

  // Rule for blocking purchase if user has any of the specified related services
  blockedByRelatedServices(relatedServices, customTooltip) {
    this.rules.push({
      type: 'blocked_by_related_services',
      value: relatedServices,
      errorTooltip: customTooltip || 'productPage.common.buyBox.buttonDisabled.tooltip'
    });
    return this;
  }

  // Rule for allowing purchase if user has any of the specified related services
  customCtaTextHasRelatedServices(relatedServices, customCtaText) {
    this.rules.push({
      type: 'custom_cta_text_has_related_services',
      value: relatedServices,
      customCtaText
    });
    return this;
  }

  disable(_, customTooltip) {
    this.rules.push({
      type: 'disable',
      errorTooltip: customTooltip || 'productPage.common.buyBox.buttonDisabled.tooltip'
    });
    return this;
  }

  // Build the final configuration function
  build() {
    return (
      entitlements,
      service,
      jurisdiction,
      formedBy,
      formationStatus
    ) => {
      // Use provided values or defaults from builder
      const finalCtaText = this.config.ctaText;
      const finalOwnedCtaText = this.config.ownedCtaText;
      const defaultTooltip = this.config.ctaDisabledTooltip;
      const displayText = finalOwnedCtaText || finalCtaText;

      // First, check if multiple purchases are allowed
      const allowsMultiplePurchases = this.rules.some((rule) => rule.type === 'allow_multiple_purchases');

      // Evaluate rules in order - stop at first failure
      for (const rule of this.rules) {
        switch (rule.type) {
          case 'jurisdiction_exact':
            if (jurisdiction !== rule.value) {
              return {
                isBuyEnabled: false,
                ctaText: finalCtaText,
                ownedCtaText: finalOwnedCtaText,
                ctaDisabledTooltip: rule.errorTooltip || defaultTooltip // Use config tooltip as fallback
              };
            }
            break;

          case 'jurisdiction_in_list':
            if (!rule.value.includes(jurisdiction)) {
              return {
                isBuyEnabled: false,
                ctaText: finalCtaText,
                ownedCtaText: finalOwnedCtaText,
                ctaDisabledTooltip: rule.errorTooltip || defaultTooltip // Use config tooltip as fallback
              };
            }
            break;

          case 'product_not_purchased':
            if (entitlements?.has(rule.value) && !allowsMultiplePurchases) {
              // Product is owned and multiple purchases not allowed
              return {
                isBuyEnabled: false,
                ctaText: displayText,
                ownedCtaText: finalOwnedCtaText,
                ctaDisabledTooltip: rule.errorTooltip || defaultTooltip // Use config tooltip as fallback
              };
            }
            break;

          case 'current_product_not_purchased':
            if (entitlements?.has(service) && !allowsMultiplePurchases) {
              // Current product is owned and multiple purchases not allowed
              return {
                isBuyEnabled: false,
                ctaText: displayText,
                ownedCtaText: finalOwnedCtaText,
                ctaDisabledTooltip: rule.errorTooltip || defaultTooltip // FIXED: Use config tooltip as fallback
              };
            }
            break;

          case 'allow_review_purchased':
            if (entitlements?.has(service) && !allowsMultiplePurchases) {
              // Current product is owned and allow navigate to url review purchased
              return {
                isBuyEnabled: true,
                ctaText: displayText,
                ownedCtaText: finalOwnedCtaText,
                allowsReviewPurchased: rule.value
              };
            }
            break;

          case 'allow_multiple_purchases':
            // This doesn't fail, just modifies behavior
            break;

          case 'formed_by_zb':
            // Keep 'customer' check for backward compatibility during migration to 'other'
            // Will allow null and undefined values [https://counsl.atlassian.net/browse/OARENG-2264]
            if (formedBy === 'other' || formedBy === 'customer') {
              return {
                isBuyEnabled: false,
                ctaText: finalCtaText,
                ownedCtaText: finalOwnedCtaText,
                ctaDisabledTooltip: rule.errorTooltip || defaultTooltip // Use config tooltip as fallback
              };
            }
            break;

          case 'business_formed':
            if (formationStatus !== 'formed') {
              return {
                isBuyEnabled: false,
                ctaText: finalCtaText,
                ownedCtaText: finalOwnedCtaText,
                ctaDisabledTooltip: rule.errorTooltip || defaultTooltip // Use config tooltip as fallback
              };
            }
            break;

          case 'blocked_by_related_services':
            if (rule.value.some((relatedService) => entitlements?.has(relatedService))) {
              return {
                isBuyEnabled: false,
                ctaText: finalCtaText,
                ownedCtaText: finalOwnedCtaText,
                ctaDisabledTooltip: rule.errorTooltip || defaultTooltip // Use config tooltip as fallback
              };
            }
            break;

          case 'requires_product':
            if (!entitlements?.has(rule.value)) {
              return {
                isBuyEnabled: false,
                ctaText: rule.customCtaText || finalCtaText,
                ownedCtaText: finalOwnedCtaText,
                ctaDisabledTooltip: rule.errorTooltip || defaultTooltip // Use config tooltip as fallback
              };
            }
            break;

          case 'custom_cta_text_has_related_services':
            if (rule.value.some((relatedService) => entitlements?.has(relatedService))) {
              return {
                isBuyEnabled: true,
                ctaText: rule.customCtaText
              };
            }
            break;
          case 'disable':
            return {
              isBuyEnabled: false,
              ctaText: finalCtaText,
              ctaDisabledTooltip: rule.errorTooltip || defaultTooltip // Use config tooltip as fallback
            };

          default:
            // Unknown rule type, skip
            break;
        }
      }

      // All rules passed - enable purchase
      return {
        isBuyEnabled: true,
        ctaText: finalCtaText,
        ownedCtaText: finalOwnedCtaText,
        ctaDisabledTooltip: defaultTooltip
      };
    };
  }
}

// Pre-configured builders for common use cases
export const globalEntitlements = (config) => new BuyButtonConfigBuilder(config);
